import { CATEGORY_SETUP, snapsToEdges, useEditor } from '../store/useEditor'
import { PRODUCTS } from '../lib/products'
import { bulbsForFeet } from '../lib/materials'
import { sectionsOf } from '../lib/sections'
import { IconChevronRight } from './icons'
import LookControls from './LookControls'
import { defaultStyleFor, garlandColors, placeableFor, stylesFor } from '../lib/decorLibrary'
import RunCard from './RunCard'
import { Slider } from './bits'

/**
 * THE PANEL THAT ONLY EXISTS WHEN THERE IS SOMETHING TO SAY.
 *
 * The rail this replaces was permanent: every control for every category, on
 * screen at all times, whether anything was selected or not. It took a third
 * of the window away from the photograph to show a person controls for a thing
 * they were not touching.
 *
 * This shows the properties of the one run you have selected, and nothing when
 * nothing is selected. Select a run, change its color, close it. That is the
 * whole interaction, and it is the same one every canvas tool that respects
 * its canvas has settled on.
 *
 * The one exception is a run that would not light - an end that runs onto the
 * side of the house, an edge nothing in the photograph settles. That is a real
 * question about the roof and it gets asked here. Everything else lights on
 * arrival and needs no panel at all.
 *
 * There is no scale here and no money here. V1 is a picture, not a quote.
 */

/**
 * HOW YOU DRAW THIS ONE, IN ONE LINE, UNDER ITS NAME.
 *
 * The reference frames put a sentence directly under Roofline: click each
 * corner of the eave, Enter finishes the run. The panel had the name of the
 * thing and no word about what to do with it, so on a first house the only
 * instruction anywhere on screen was a hint floating over the photograph -
 * which is the line that has since been deleted for saying the wrong thing on
 * an empty photo. This says the right thing, in the place a person is already
 * looking, and it is specific to what is armed.
 */
const HOW: Partial<Record<string, string>> = {
  roofline: 'Click each corner of the eave. Enter finishes the run.',
  garland: 'Click along the rail or header. Enter finishes the run.',
  palms: 'Click the base of the trunk, then the top.',
  trees: 'Click the base, then the top.',
  clusters: 'Click where each strand hangs from.',
  wreaths: 'Click on the photo to place it.',
  columns: 'Click the bottom of the column, then the top.',
  accessories: 'Click on the photo to place it.',
}

/**
 * A PALM IS TWO JOBS AND THIS IS THE SWITCH BETWEEN THEM.
 *
 * The trunk is two taps and a spiral. The crown is a strand run out along each
 * frond that is ALREADY GROWING in the photograph, traced one leaf at a time,
 * so it is a different stroke rather than a different slider. What used to be
 * here - a checkbox that drew an even starburst of spokes above the trunk -
 * was the one thing in this app inventing light where the photo had none.
 *
 * IT IS ALWAYS ON SCREEN WHILE PALMS IS ARMED, and that is the whole point of
 * it living out here instead of inside one branch. It lived in the
 * nothing-selected branch for a commit, which meant it disappeared the moment
 * a run was drawn - because drawing selects what you drew. So the panel came
 * back showing the trunk's own color grid, the switch was gone, and pressing
 * where it used to be recolored the trunk instead. Reported as: it starts
 * changing the color of the trunk I just did, it does not switch to fronds, I
 * do not know how to start.
 *
 * This is a question about the NEXT stroke, not about the selected run, so it
 * cannot be hidden by having a run selected.
 */
function PalmMode() {
  const category = useEditor((s) => s.category)
  const palmMode = useEditor((s) => s.palmMode)
  const setPalmMode = useEditor((s) => s.setPalmMode)
  if (category !== 'palms') return null

  return (
    <div className="insp-mode">
      <div className="seg wrap">
        <button
          aria-pressed={palmMode === 'trunk'}
          onClick={() => setPalmMode('trunk')}
          title="Two taps: bottom of the trunk, then the top"
        >
          Trunk
        </button>
        <button
          aria-pressed={palmMode === 'fronds'}
          onClick={() => setPalmMode('fronds')}
          title="Draw along a frond that is already in the photo"
        >
          Fronds
        </button>
      </div>
      <p className="insp-note">
        {palmMode === 'trunk'
          ? 'Click the bottom of the trunk, then the top.'
          : 'Drag along one frond at a time, following the leaf in the photo.'}
      </p>
    </div>
  )
}

export default function Inspector({ onBack }: { onBack?: () => void } = {}) {
  const doc = useEditor((s) => s.doc)
  const present = useEditor((s) => s.present)
  const selectedId = useEditor((s) => s.selectedId)
  const selectedProposalId = useEditor((s) => s.selectedProposalId)
  const category = useEditor((s) => s.category)
  const defaults = useEditor((s) => s.defaults)
  const selectLayer = useEditor((s) => s.selectLayer)
  const deleteLayer = useEditor((s) => s.deleteLayer)
  const duplicateLayer = useEditor((s) => s.duplicateLayer)
  const updateStrand = useEditor((s) => s.updateStrand)
  const updateDecoration = useEditor((s) => s.updateDecoration)
  const setDecorStyle = useEditor((s) => s.setDecorStyle)
  const decorStyle = useEditor((s) => s.decorStyle)
  const setDefaults = useEditor((s) => s.setDefaults)
  const setCategoryLook = useEditor((s) => s.setCategoryLook)
  const palmMode = useEditor((s) => s.palmMode)
  const setPalmMode = useEditor((s) => s.setPalmMode)
  const tool = useEditor((s) => s.tool)
  const snapEnabled = useEditor((s) => s.snapEnabled)
  const setSnap = useEditor((s) => s.setSnap)

  const selected = doc.layers.find((l) => l.id === selectedId)
  const strand = selected && selected.type === 'strand' ? selected : null
  const decor = selected && selected.type === 'decoration' ? selected : null

  /* Snapping is about following a real edge in the photograph, so it is only
     offered where there is an edge to follow. The store owns that answer, at
     the call that actually hands the straightener an edge map. */
  const snappable = snapsToEdges({ category, palmMode }, tool)
  const runCount = doc.layers.length

  /*
   * WHAT THIS RUN IS. Bulbs always, because the renderer places them and the
   * count is a fact about the picture. Feet only when a measured section is
   * attached: a run has pixels until somebody walks it, and a foot count
   * worked out from pixels is a guess wearing the costume of a measurement.
   */
  const runFacts = (() => {
    if (!strand) return ''
    const sec = sectionsOf(doc).find((x) => x.runIds?.includes(strand.id))
    const feet = sec?.measurement?.feet ?? 0
    const walked = feet > 0 && sec?.measurement?.source === 'field'
    const inches = PRODUCTS[CATEGORY_SETUP[category].product]?.spacingInches ?? 12
    if (!walked) return 'Not measured at the house yet'
    const bulbs = bulbsForFeet(feet, inches)
    return `${Math.round(feet)} ft, ${bulbs} ${bulbs === 1 ? 'bulb' : 'bulbs'}`
  })()

  // Same rule as the bar: the upload screen owns the window until there is a
  // photograph to put a panel on top of.
  if (present || !doc.photo) return null

  /*
   * NOTHING SELECTED, NOTHING ON SCREEN.
   *
   * This used to open the panel whenever the document held any proposal at
   * all, which was right when the panel listed every run waiting to be
   * included. That list is gone and RunCard draws only the SELECTED run, so
   * the condition was outliving its content: a proposal existed, the aside
   * rendered, and the card inside it returned null - an empty rounded box in
   * the corner of the photograph with nothing in it. Reported as: are these
   * things necessary.
   *
   * The panel opens for the one run being looked at, and for nothing else.
   */
  if (!strand && !decor) {
    const run = doc.proposals.some((p) => p.id === selectedProposalId)
    if (run) {
      return (
        <aside className="inspector">
          <RunCard />
        </aside>
      )
    }

    /*
     * NOTHING SELECTED: THE SETTINGS FOR WHAT YOU ARE ABOUT TO DRAW.
     *
     * The panel has one fixed home and always answers the same question -
     * what am I working with, and what does it look like. Before a run
     * exists that is the category and the defaults; after you select one it
     * is that run. Same place, same shape, so nobody has to hunt.
     *
     * These controls lived in a popover over the middle of the house for a
     * while. A panel that covers the thing you are looking at is the one
     * thing every canvas tool has learned not to do, and it read exactly as
     * wrong as it was.
     *
     * It is deliberately NOT a list of your runs. That was here once, as a
     * row per run with an Include button, and it was clutter: the runs are
     * on the photograph, which is where you point at one.
     */
    /*
     * A CATEGORY THAT PLACES A THING SHOWS THE THINGS.
     *
     * Wreaths and Accessories do not draw a strand, they put down a
     * photographed object - and the panel was showing them a grid of bulb
     * colors, which is a setting for something they do not have. So picking
     * Accessories gave no bow, no picture, nothing to choose and no hint that
     * a bow existed at all. Reported as: still do not see bows, nothing under
     * accessories.
     *
     * It shows what it is about to place, and pressing one arms it. Straight
     * from the library, so a second bow or a reindeer is still one row in
     * decorLibrary and nothing else.
     */
    const places = placeableFor(category)
    if (places.length) {
      return (
        <aside className="inspector">
          {onBack && (
            <button className="inspector-back" onClick={onBack}>
              &lsaquo; All lighting
            </button>
          )}
          <header className="insp-head">
            <h3>{CATEGORY_SETUP[category].label}</h3>
          </header>
          <p className="insp-lbl plain">Which one</p>
          <div className="insp-products">
            {places.map((st) => (
              <button
                key={st.id}
                className="product"
                aria-pressed={(decorStyle[st.kind] ?? defaultStyleFor(st.kind)?.id) === st.id}
                onClick={() => setDecorStyle(st.kind, st.id)}
              >
                <img src={st.url} alt="" loading="lazy" />
                <span>{st.label}</span>
              </button>
            ))}
          </div>
          <p className="insp-note">Click on the photo to place it.</p>
        </aside>
      )
    }

    return (
      <aside className="inspector">
        {/*
          THE WAY BACK TO THE LIST. The reference has it as a small link above
          the panel heading, because the left column is one place showing two
          things and a person needs to be able to get back to the first.
        */}
        {onBack && (
          <button className="inspector-back" onClick={onBack}>
            &lsaquo; All lighting
          </button>
        )}
        <header className="insp-head">
          <h3>{CATEGORY_SETUP[category].label}</h3>
        </header>

        {HOW[category] && <p className="insp-how">{HOW[category]}</p>}

        <PalmMode />

        {/*
          EVERY CATEGORY THAT DRAWS BULBS GETS THE BULB CONTROLS.
          
          Spacing and size were shown for rooflines and garland only, so
          picking Palms or Trees gave a panel with a color grid and nothing
          else - and a tree wrap is a line of bulbs with a spacing exactly like
          a fascia is. Reported as: the settings for the other items I do not
          see on the side, such as the tree lights.
          
          Wreaths and accessories still do not get them, because they place a
          photograph of an object rather than draw a strand. Their look is a
          picture, not a palette.
        */}
        <LookControls
          look={defaults}
          product={PRODUCTS[CATEGORY_SETUP[category].product]?.label}
          spacing={category !== 'wreaths' && category !== 'accessories'}
          /* Thickness and the cap are the trunk's. A frond is a line of bulbs
             along a leaf and has neither. */
          thickness={(category === 'palms' && palmMode === 'trunk') || category === 'trees'}
          cap={category === 'palms' && palmMode === 'trunk'}
          onChange={setCategoryLook}
          beforeMore={
            <>
              {/*
          SNAP LIVED ON THE ADVANCED RAIL, WHICH IS A MODE NOBODY IS IN.

          It is the one setting on this panel that changes how a click behaves
          rather than how a run looks, and the reference frames put it right
          here under the color, with a switch. It was only reachable from a
          rail that no longer shows by default, so for every person in simple
          view it was a feature that existed and could not be turned off.
        */}
        {snappable && (
          <label className="insp-switch">
            <span>Snap to roof edge</span>
            <input
              type="checkbox"
              checked={snapEnabled}
              onChange={(e) => setSnap(e.target.checked)}
            />
            <i aria-hidden="true" />
          </label>
        )}
            </>
          }
        />


        {/*
          WHAT IS ALREADY ON THE PHOTOGRAPH, counted, at the foot of the panel.
          A person who has drawn four runs and is looking at a picture where
          two of them are behind a palm has no way to know there are four.
        */}
        {runCount > 0 && (
          <button className="insp-runs" onClick={() => selectLayer(null)}>
            <span>
              {runCount} {runCount === 1 ? 'run' : 'runs'} on the photo
            </span>
            <IconChevronRight size={15} />
          </button>
        )}
      </aside>
    )
  }

  const close = () => selectLayer(null)
  const remove = () => {
    if (selected) deleteLayer(selected.id)
    selectLayer(null)
  }

  /* ------------------------------------------------------------ a drawn run */

  if (strand) {
    /*
     * A wrap is not here: its thickness is the trunk's, and that now sits with
     * the rest of the look, in one place, named for what it is. Two sliders
     * for one number in two different sections is how a panel gets confusing.
     */
    const sizeable =
      strand.kind === 'swag' || strand.kind === 'cluster' || strand.kind === 'freeform'


    return (
      <aside className="inspector">
        {/* First, because it is about the next stroke and never about this run. */}
        <PalmMode />

        {/*
          THE HEADING IS THE ITEM'S OWN NAME AND WHAT YOU ARE DOING TO IT,
          which is how the design package writes it: a title, a small line
          under it saying Editing selected item, and the close on the right.
          Ours had the name and nothing else, so the panel never said whether
          it was about the thing you had selected or the thing you were about
          to draw - and those are the two states it has.
        */}
        {/*
          THE FACTS ABOUT THIS RUN, NOT A LABEL ABOUT THE PANEL.

          It said Editing selected item, which is the panel describing itself:
          a person can see they have something selected, because they selected
          it. The reference frames print what the run IS - 74 ft, 74 bulbs.

          Feet only appears when somebody walked it. A run has pixels, not
          feet, until a measured section is attached to it, and a foot count
          worked out from pixels is a guess wearing the costume of a
          measurement. Unknown is written as the word.
        */}
        <header className="insp-head">
          <div>
            <h3>{strand.name}</h3>
            <small>{runFacts}</small>
          </div>
          <button className="insp-close" onClick={close} aria-label="Close">
            &times;
          </button>
        </header>

        {/*
          The same control the Add sheet shows before you draw. Changing it
          here changes THIS run and also the next one: somebody who turns a run
          red is doing the red house, not making one exception, and making them
          set it again on every run is how a five run house takes five
          corrections.
        */}
        <LookControls
          look={strand}
          product={PRODUCTS[CATEGORY_SETUP[category].product]?.label}
          moreLabel="Adjust"
          spacing
          thickness={strand.kind === 'wrap'}
          cap={strand.kind === 'wrap'}
          onChange={(patch) => {
            updateStrand(strand.id, patch)
            setDefaults(patch)
          }}
        />

        {sizeable && (
          <>
            <p className="insp-lbl">Size</p>
            <Slider
              label={
                strand.kind === 'swag'
                  ? 'How thick'
                  : strand.kind === 'cluster'
                    ? 'How far apart'
                    : strand.kind === 'freeform'
                      ? 'How big an area'
                      : 'Trunk thickness'
              }
              value={strand.wrapWidth}
              min={0.006}
              max={0.14}
              step={0.002}
              onChange={(v) => updateStrand(strand.id, { wrapWidth: v })}
              /*
               * A plain one to ten. It used to read real inches when the photo
               * had a scale, and there is no scale step in V1 - a number that
               * appears for some photos and not others is worse than a number
               * that is always the same kind of number.
               */
              format={(v) => `${Math.round(((v - 0.006) / (0.14 - 0.006)) * 9) + 1} of 10`}
            />
          </>
        )}

        {strand.kind === 'swag' && (
          <>
            <p className="insp-lbl">Where is it</p>
            <div className="seg wrap">
              {(
                [
                  ['flush', 'Flat on', 'Garage header, door or window frame'],
                  ['swags', 'Swagged', 'Porch rail or fascia, dipping between posts'],
                  ['wrapped', 'Wrapped', 'Spiraled round a post or column'],
                ] as const
              ).map(([id, label, hint]) => (
                <button
                  key={id}
                  aria-pressed={(strand.garlandShape ?? 'flush') === id}
                  title={hint}
                  onClick={() =>
                    updateStrand(strand.id, {
                      garlandShape: id,
                      // A swag with no dip is a flat run wearing the wrong
                      // name, so give it one the moment it is chosen.
                      ...(id === 'swags' && strand.sag < 0.05 ? { sag: 0.35 } : {}),
                    })
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {(strand.garlandShape ?? 'flush') === 'swags' && (
              <>
                <Slider
                  label="How many dips"
                  value={strand.swagCount ?? 4}
                  min={1}
                  max={12}
                  step={1}
                  onChange={(v) => updateStrand(strand.id, { swagCount: v })}
                  format={(v) => `${v.toFixed(0)}`}
                />
                <Slider
                  label="How deep they hang"
                  value={strand.sag}
                  min={0.05}
                  max={1}
                  step={0.05}
                  onChange={(v) => updateStrand(strand.id, { sag: v })}
                  format={(v) => `${Math.round(v * 100)}%`}
                />
              </>
            )}

            {strand.garlandShape === 'wrapped' && (
              <Slider
                label="How tight the spiral"
                value={strand.wrapTurns}
                min={4}
                max={40}
                step={1}
                onChange={(v) => updateStrand(strand.id, { wrapTurns: v })}
                format={(v) => `${v.toFixed(0)} turns`}
              />
            )}
          </>
        )}

        {strand.kind === 'wrap' && (
          <>
            <Slider
              label="How tight the wrap"
              value={strand.wrapTurns}
              min={4}
              max={40}
              step={1}
              onChange={(v) => updateStrand(strand.id, { wrapTurns: v })}
              format={(v) => `${v.toFixed(0)} turns`}
            />

            {/*
              THE CROWN USED TO BE HERE, as a checkbox called Light the fronds
              plus a palm preset, a frond count, a length and an arch. It drew
              a starburst of evenly spaced spokes above the trunk - invented
              geometry, related to nothing in the photograph, and the one thing
              in this app that put light where there was none.

              A real palm is lit by running a strand out along each frond that
              is already growing there, so that is a drawing tool rather than a
              slider, and it is the Fronds mode under Palms. What survives here
              is the CAP, which is a collar of a different color on the trunk
              wrap itself, and it lives with the rest of the look.
            */}
          </>
        )}

        {/*
          DUPLICATE AND REMOVE, side by side and both quiet, as the package
          draws them. Delete this run was a red word on its own at the foot of
          the panel: the loudest thing in it was the one action nobody can
          undo, and the thing a person actually reaches for more often -
          another run just like this one - was not there at all.
        */}
        <div className="panel-actions">
          <button className="panel-act" onClick={() => duplicateLayer(strand.id)}>
            Duplicate
          </button>
          <button className="panel-act danger" onClick={remove}>
            Delete
          </button>
        </div>
      </aside>
    )
  }

  /* --------------------------------------------------- a placed decoration */

  /*
   * WHICH ONE, FOR ANYTHING THERE IS MORE THAN ONE OF.
   *
   * This named wreaths and garland and stopped, so a placed bow got no picker
   * at all. Harmless while there is exactly one bow photographed, and a dead
   * end the moment a second one is added: the library is built so that adding
   * a row is the whole job - "adding a reindeer means adding one row above,
   * not touching the interface" - and this was the one place that did not hold
   * up its end of that.
   *
   * Anything with only one style still shows nothing, because a choice between
   * one thing is not a choice.
   */
  const products =
    decor!.kind === 'garland' ? garlandColors() : stylesFor(decor!.kind)

  return (
    <aside className="inspector">
      <header className="insp-head">
        <h3>{decor!.name}</h3>
        <button className="insp-close" onClick={close} aria-label="Close">
          &times;
        </button>
      </header>

      {products.length > 1 && (
        <>
          <p className="insp-lbl">Which one</p>
          <div className="insp-products">
            {products.map((st) => (
              <button
                key={st.id}
                className="product"
                aria-pressed={decor!.styleId === st.id}
                onClick={() => {
                  updateDecoration(decor!.id, { styleId: st.id })
                  // The next one you place is almost always the same one.
                  setDecorStyle(decor!.kind, st.id)
                }}
              >
                <img src={st.url} alt="" loading="lazy" />
                <span>{st.label}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <p className="insp-lbl">Size</p>
      {decor!.kind === 'garland' ? (
        <>
          <Slider
            label="Length"
            value={decor!.scale}
            min={0.6}
            max={9}
            step={0.05}
            onChange={(v) => updateDecoration(decor!.id, { scale: v })}
            format={(v) => `${Math.round(((v - 0.6) / (9 - 0.6)) * 9) + 1} of 10`}
          />
          <Slider
            label="Thickness"
            value={decor!.scaleY ?? decor!.scale}
            min={0.3}
            max={4}
            step={0.05}
            onChange={(v) => updateDecoration(decor!.id, { scaleY: v })}
            format={(v) => `${Math.round(((v - 0.3) / (4 - 0.3)) * 9) + 1} of 10`}
          />
        </>
      ) : (
        <>
          <Slider
            label="Size"
            value={decor!.scale}
            min={0.3}
            max={5}
            step={0.05}
            onChange={(v) => updateDecoration(decor!.id, { scale: v, scaleY: undefined })}
            format={(v) => `${Math.round(((v - 0.3) / (5 - 0.3)) * 9) + 1} of 10`}
          />
          {decor!.kind === 'wreath' && (
            <Slider
              label="How many bulbs"
              value={decor!.extraBulbs ?? 0}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => updateDecoration(decor!.id, { extraBulbs: v })}
              format={(v) => (v < 0.03 ? 'as photographed' : 'fuller')}
            />
          )}
        </>
      )}

      <p className="insp-note">Drag it on the photo to move it.</p>

      <button className="insp-del" onClick={remove}>
        Delete this one
      </button>
    </aside>
  )
}
